/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */
import { Checkbox } from "@cmp-src/primitives/checkbox";
import { cn } from "@gettactic/helpers/src/css/cn";
import { Dict } from "@gettactic/helpers/src/types/common";
import {
  Draggable,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  Droppable
} from "@hello-pangea/dnd";
import React from "react";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { useDataMan } from "../../hooks/useDataMan";
import { useDataTableFixedHeader } from "../../hooks/useDataTableFixedHeader";
import { DataManCellProvider } from "../../providers/DataManCellProvider";
import { DataManColumnProvider } from "../../providers/DataManColumnProvider";
import { DataManRowProvider } from "../../providers/DataManRowProvider";
import { HierarchyItem } from "../../types/data";
import { DataManBulkActionsFooter } from "../common/DataManBulkActionsFooter";
import { DataManCellContent } from "../common/DataManCellContent";
import { DataManHeaderColumnContent } from "../common/DataManHeaderColumnContent";
import { DataManHierarchyRowActions } from "../common/DataManHierarchyRowActions";
import { DataManLoadMore } from "../common/DataManLoadMore";
import { DataManRowActions } from "../common/DataManRowActions";
import { DataManSelectControl } from "../common/DataManSelectControl";
import { DataManSortableHandler } from "../common/DataManSortableHandler";
import { DATA_MAN_CHECKBOX_CLASS } from "../common/dataManCheckboxClass";
import { DataManTableViewHeader } from "./DataManTableViewHeader";
import { DataManTableViewHierarchyHeader } from "./DataManTableViewHierarchyHeader";

const classNames = {
  Line: "",
  RootLine: "border-b",
  ItemLine: "border-b",
  ItemsContainer: "border-b",
  DataItemRowCell: ""
};

/**
 * The drag handle props make their element a button. A row or cell stays part of the table
 * without it, still focusable and described by the drag instructions.
 */
const withoutButtonRole = (
  dragHandleProps: DraggableProvided["dragHandleProps"] | undefined
) => {
  if (!dragHandleProps) return undefined;
  const { role: _role, ...rest } = dragHandleProps;
  return rest;
};

// Context to share captured column widths between row and drag clone
const DragWidthsContext = React.createContext<React.MutableRefObject<
  number[]
> | null>(null);

// Drag clone row component - renders a simplified row for the drag preview
// This is separate from the main row to avoid the max-w-0 issue during drag
interface DragCloneRowProps {
  // biome-ignore lint/suspicious/noExplicitAny: data can be any shape
  data: any;
  provided: DraggableProvided;
  depth?: number;
}

const DragCloneRow: React.FC<DragCloneRowProps> = ({ data, provided }) => {
  const {
    visibleColumns,
    hasSortableDataColumns,
    hasActions,
    hasBulkSelection
  } = useDataMan();
  const widthsRef = React.useContext(DragWidthsContext);
  const widths = widthsRef?.current || [];

  let cellIndex = 0;

  return (
    <DataManRowProvider data={data}>
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className="flex items-center bg-white border border-neutral-300 rounded-lg shadow-md"
      >
        {hasSortableDataColumns && (
          <DataManColumnProvider isSortableHandler>
            <DataManCellProvider value={<DataManSortableHandler />}>
              <div style={{ width: widths[cellIndex++] || "auto" }}>
                <DataManCellContent />
              </div>
            </DataManCellProvider>
          </DataManColumnProvider>
        )}
        {hasBulkSelection && (
          <DataManColumnProvider isSelectionColumn>
            <DataManCellProvider value={<DataManSelectControl />}>
              <div style={{ width: widths[cellIndex++] || "auto" }}>
                <DataManCellContent />
              </div>
            </DataManCellProvider>
          </DataManColumnProvider>
        )}
        {visibleColumns?.map((column) => {
          const value = column.render?.(data) || data[column.key];
          const width = widths[cellIndex++] || "auto";
          return (
            <DataManColumnProvider column={column} key={column.key}>
              <DataManCellProvider value={value}>
                <div style={{ width }} key={column.key}>
                  <DataManCellContent />
                </div>
              </DataManCellProvider>
            </DataManColumnProvider>
          );
        })}
        {hasActions && (
          <DataManColumnProvider isActionColumn>
            <DataManCellProvider value={<DataManRowActions />}>
              <div style={{ width: widths[cellIndex] || "auto" }}>
                <DataManCellContent />
              </div>
            </DataManCellProvider>
          </DataManColumnProvider>
        )}
      </div>
    </DataManRowProvider>
  );
};

// Hierarchy drag clone row component
const HierarchyDragCloneRow: React.FC<DragCloneRowProps> = ({
  data,
  provided,
  depth = 0
}) => {
  const {
    visibleHierarchyColumns,
    hasHierarchyActions,
    hasSortableDataColumns,
    isHierarchyCollapsable
  } = useDataMan();
  const widthsRef = React.useContext(DragWidthsContext);
  const widths = widthsRef?.current || [];

  let cellIndex = 0;

  return (
    <DataManRowProvider data={data}>
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className="flex items-center bg-white border border-neutral-300 rounded-lg shadow-md"
      >
        {hasSortableDataColumns && (
          <DataManColumnProvider isSortableHandler>
            <DataManCellProvider value={<DataManSortableHandler />}>
              <div style={{ width: widths[cellIndex++] || "auto" }}>
                <DataManCellContent />
              </div>
            </DataManCellProvider>
          </DataManColumnProvider>
        )}
        {visibleHierarchyColumns?.map((column, colIndex) => {
          const value = column.render?.(data) || data[column.key];
          const width = widths[cellIndex++] || "auto";
          const isFirstColumn = colIndex === 0;
          return (
            <DataManColumnProvider column={column} key={column.key}>
              <DataManCellProvider value={value}>
                <div
                  style={{
                    width,
                    paddingLeft:
                      isFirstColumn && depth > 0 ? `${depth * 10}px` : undefined
                  }}
                  key={column.key}
                >
                  <DataManCellContent
                    hasCollapseControl={isHierarchyCollapsable && isFirstColumn}
                  />
                </div>
              </DataManCellProvider>
            </DataManColumnProvider>
          );
        })}
        {hasHierarchyActions && (
          <DataManColumnProvider isActionColumn>
            <DataManCellProvider value={<DataManHierarchyRowActions />}>
              <div style={{ width: widths[cellIndex] || "auto" }}>
                <DataManCellContent />
              </div>
            </DataManCellProvider>
          </DataManColumnProvider>
        )}
      </div>
    </DataManRowProvider>
  );
};

interface HierarchyItemItemProps {
  item: HierarchyItem;
  depth: number;
  index: number;
  isFirstRow?: boolean;
  isLastRow?: boolean;
}

interface DataManTableViewRowProps {
  // biome-ignore lint/suspicious/noExplicitAny: TODO FIX IT
  data: any;
  index: number;
  isSelected: boolean;
  isFirstRow: boolean;
  isLastRow: boolean;
}

const DataManTableViewRowInner: React.FC<DataManTableViewRowProps> = ({
  data,
  index,
  isSelected,
  isFirstRow,
  isLastRow
}) => {
  const {
    visibleColumns,
    hasSortableDataColumns,
    hasActions,
    hasBulkSelection,
    clickRecord,
    showSelectedBackground,
    isRecordClickable,
    actionsColumnWidth,
    fullBleed,
    getRecordAccentColor,
    toggleSelectItem,
    dataIdKey,
    rowMinHeight
  } = useDataMan();
  const widthsRef = React.useContext(DragWidthsContext);
  const rowRef = React.useRef<HTMLTableRowElement>(null);

  // `height` on a table row acts as a minimum height — rows still grow to fit
  // taller content — so it's the right lever for a global min row height.
  const rowHeightStyle: React.CSSProperties | undefined =
    rowMinHeight != null
      ? {
          height:
            typeof rowMinHeight === "number"
              ? `${rowMinHeight}px`
              : rowMinHeight
        }
      : undefined;

  const hasSelectedBg = isSelected && showSelectedBackground;
  // Per-row accent bar, rendered flush at the very start of the row (before any
  // selection checkbox) inside the leading cell. Absolutely positioned so it
  // never shifts the row layout.
  const accentColor = getRecordAccentColor?.(data, index);

  // Clicking anywhere in the selection cell toggles the row and never triggers
  // the row-body action (e.g. opening a detail modal). The checkbox input stops
  // its own propagation, so this handler covers the surrounding cell area.
  const rowItemId = String(data?.[dataIdKey || "id"] ?? "");
  const handleSelectionCellClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (rowItemId) toggleSelectItem(rowItemId);
  };

  const rowClassName = cn(
    "group",
    isRecordClickable && "cursor-pointer",
    hasSelectedBg
      ? "bg-neutral-50 hover:bg-neutral-200"
      : "bg-white hover:bg-neutral-50"
  );

  // Capture cell widths on mousedown for the drag preview
  const handleMouseDown = () => {
    if (rowRef.current && widthsRef) {
      const cells = rowRef.current.querySelectorAll("td");
      widthsRef.current = Array.from(cells).map((cell) => cell.offsetWidth);
    }
  };

  const renderRowCells = (
    className: string,
    provided?: DraggableProvided,
    snapshot?: DraggableStateSnapshot
  ) => {
    let cellIndex = 0;
    // Calcola il numero totale di celle per determinare l'ultima
    const totalCells =
      (hasSortableDataColumns ? 1 : 0) +
      (hasBulkSelection ? 1 : 0) +
      (visibleColumns?.length || 0) +
      (hasActions ? 1 : 0);

    // Helper function per generare le classi comuni delle celle
    const getCellClassName = (
      currentIndex: number,
      isDataColumn?: boolean,
      isActionColumn?: boolean
    ) => {
      return cn(
        classNames.DataItemRowCell,
        // Apply max-w-0 only to data columns to allow truncation without growing the table
        // Don't apply to sortable handler, selection column, or actions column
        isDataColumn && "overflow-hidden max-w-0",
        // Actions column should maintain its width and not shrink
        isActionColumn && "overflow-visible",
        // Card frame (outer borders + rounded corners) only in the default
        // variant. fullBleed renders edge-to-edge inside its host container, so
        // it drops the frame and keeps just the horizontal row separators.
        !fullBleed && currentIndex === 0 && "border-l border-l-neutral-300",
        !fullBleed &&
          currentIndex === totalCells - 1 &&
          "border-r border-r-neutral-300",
        !fullBleed && isLastRow && "border-b border-b-neutral-300",
        !fullBleed && isFirstRow && currentIndex === 0 && "rounded-tl-lg",
        !fullBleed && isLastRow && currentIndex === 0 && "rounded-bl-lg",
        !fullBleed &&
          isLastRow &&
          currentIndex === totalCells - 1 &&
          "rounded-br-lg",
        !fullBleed &&
          isFirstRow &&
          currentIndex === totalCells - 1 &&
          "rounded-tr-lg",
        !fullBleed && isFirstRow && "border-t border-t-neutral-300",
        !isLastRow && "border-b border-b-separator"
      );
    };

    // Helper function per renderizzare una cella
    const renderCell = (
      content: React.ReactNode,
      providerProps: {
        isSortableHandler?: boolean;
        isSelectionColumn?: boolean;
        column?: any;
        isActionColumn?: boolean;
      },
      currentIndex: number
    ) => {
      const { isSortableHandler, isSelectionColumn, column, isActionColumn } =
        providerProps;

      // Only the very first cell of the row carries the accent bar, so it sits
      // flush at the row's start whatever the leading cell is (checkbox,
      // sortable handle or first data column). Padding never offsets an
      // absolutely positioned child, so `left-0` is the true row edge.
      const leadingBar =
        currentIndex === 0 && accentColor ? (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 z-[1] w-[3px]"
            style={{ backgroundColor: accentColor }}
          />
        ) : null;

      let provider: React.ReactNode;
      if (isSortableHandler) {
        provider = (
          <DataManColumnProvider isSortableHandler>
            <DataManCellProvider value={content}>
              <td
                className={cn(
                  getCellClassName(currentIndex, false),
                  leadingBar && "relative"
                )}
              >
                {leadingBar}
                <DataManCellContent />
              </td>
            </DataManCellProvider>
          </DataManColumnProvider>
        );
      } else if (isSelectionColumn) {
        provider = (
          <DataManColumnProvider isSelectionColumn>
            <DataManCellProvider value={content}>
              <td
                className={cn(
                  getCellClassName(currentIndex, false),
                  leadingBar && "relative",
                  "cursor-pointer"
                )}
                onClick={handleSelectionCellClick}
              >
                {leadingBar}
                <DataManCellContent />
              </td>
            </DataManCellProvider>
          </DataManColumnProvider>
        );
      } else if (column) {
        provider = (
          <DataManColumnProvider column={column} key={column.key}>
            <DataManCellProvider value={content}>
              <td
                className={cn(
                  getCellClassName(currentIndex, true),
                  leadingBar && "relative"
                )}
                key={column.key}
              >
                {leadingBar}
                <DataManCellContent />
              </td>
            </DataManCellProvider>
          </DataManColumnProvider>
        );
      } else if (isActionColumn) {
        const actionWidth = actionsColumnWidth
          ? typeof actionsColumnWidth === "number"
            ? `${actionsColumnWidth}px`
            : actionsColumnWidth
          : undefined;
        provider = (
          <DataManColumnProvider isActionColumn>
            <DataManCellProvider value={content}>
              <td
                className={cn(
                  getCellClassName(currentIndex, false, true),
                  leadingBar && "relative"
                )}
                style={
                  actionWidth
                    ? { width: actionWidth, maxWidth: actionWidth }
                    : undefined
                }
              >
                {leadingBar}
                <DataManCellContent />
              </td>
            </DataManCellProvider>
          </DataManColumnProvider>
        );
      }

      return provider;
    };

    // Combine refs if provided exists
    const setRefs = (el: HTMLTableRowElement | null) => {
      if (rowRef) rowRef.current = el;
      if (provided?.innerRef) provided.innerRef(el);
    };

    return (
      <tr
        ref={setRefs}
        {...provided?.draggableProps}
        {...withoutButtonRole(provided?.dragHandleProps)}
        style={{ ...provided?.draggableProps?.style, ...rowHeightStyle }}
        className={cn(className, rowClassName)}
        onClick={() => clickRecord(data)}
        onMouseDown={handleMouseDown}
      >
        {hasSortableDataColumns &&
          renderCell(
            <DataManSortableHandler />,
            { isSortableHandler: true },
            cellIndex++
          )}
        {hasBulkSelection &&
          renderCell(
            <DataManSelectControl />,
            { isSelectionColumn: true },
            cellIndex++
          )}
        {visibleColumns?.map((column) => {
          const value = column.render?.(data) || data[column.key];
          return renderCell(value, { column }, cellIndex++);
        })}
        {hasActions &&
          renderCell(
            <DataManRowActions />,
            { isActionColumn: true },
            cellIndex
          )}
      </tr>
    );
  };

  if (hasSortableDataColumns) {
    return (
      <DataManRowProvider data={data}>
        <Draggable draggableId={data.id} index={Number(index)} key={data.id}>
          {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) =>
            renderRowCells("", provided, snapshot)
          }
        </Draggable>
      </DataManRowProvider>
    );
  }

  return (
    <DataManRowProvider data={data}>{renderRowCells("")}</DataManRowProvider>
  );
};

// Memoize the row component to prevent unnecessary re-renders
const MemoizedDataManTableViewRow = React.memo(DataManTableViewRowInner);

// Wrapper component that computes selection state
interface DataManTableViewRowWrapperProps {
  // biome-ignore lint/suspicious/noExplicitAny: TODO FIX IT
  data: any;
  index: number;
}

const DataManTableViewRow: React.FC<DataManTableViewRowWrapperProps> = ({
  data,
  index
}) => {
  const {
    hasBulkSelection,
    isItemSelected,
    dataIdKey,
    data: allData
  } = useDataMan();

  const idKey = dataIdKey || "id";
  const itemId = String(data?.[idKey] || "");
  const isSelected = hasBulkSelection && isItemSelected(itemId);
  const isFirstRow = index === 0;
  const isLastRow = allData ? index === allData.length - 1 : false;

  return (
    <MemoizedDataManTableViewRow
      data={data}
      index={index}
      isSelected={isSelected}
      isFirstRow={isFirstRow}
      isLastRow={isLastRow}
    />
  );
};

const HierarchyItemItem: React.FC<HierarchyItemItemProps> = ({
  item,
  depth,
  index,
  isFirstRow = false,
  isLastRow = false
}) => {
  const isRootItem = !depth;
  const {
    isHierarchyCollapsable,
    visibleHierarchyColumns,
    containerRef,
    hasActions,
    openHierarchyItems,
    hasHierarchyActions,
    clickHierarchyRecord,
    hierarchyEmptyView,
    hasSortableDataColumns,
    actionsColumnWidth
  } = useDataMan();
  const widthsRef = React.useContext(DragWidthsContext);
  const rowRef = React.useRef<HTMLTableRowElement>(null);

  // Capture cell widths on mousedown for the drag preview
  const handleMouseDown = () => {
    if (rowRef.current && widthsRef) {
      const cells = rowRef.current.querySelectorAll("td");
      widthsRef.current = Array.from(cells).map((cell) => cell.offsetWidth);
    }
  };

  const isRowExpanded = openHierarchyItems?.includes(item.id);
  const { maxHierarchyDepth } = useDataMan();

  // Check if we can show collapse control (only if not at max depth and has children)
  const canShowCollapseControl =
    isHierarchyCollapsable &&
    (maxHierarchyDepth === undefined || depth < maxHierarchyDepth) &&
    item.children &&
    item.children.length > 0;

  // Calculate total cells for border logic
  const totalCells =
    (hasSortableDataColumns ? 1 : 0) +
    (visibleHierarchyColumns?.length || 0) +
    (hasHierarchyActions ? 1 : 0);

  // Helper function to get cell className for hierarchy rows
  const getHierarchyCellClassName = (
    currentIndex: number,
    isActionColumn?: boolean,
    isSortableHandler?: boolean
  ) => {
    return cn(
      classNames.DataItemRowCell,
      // Apply max-w-0 only to data columns (not handler or action columns)
      !isActionColumn && !isSortableHandler && "overflow-hidden max-w-0",
      // Actions column should maintain its width
      isActionColumn && "overflow-visible",
      // Borders - same logic as normal table
      currentIndex === 0 && "border-l border-l-neutral-300",
      currentIndex === totalCells - 1 && "border-r border-r-neutral-300",
      isLastRow && "border-b border-b-neutral-300",
      isFirstRow && currentIndex === 0 && "rounded-tl-lg",
      isLastRow && currentIndex === 0 && "rounded-bl-lg",
      isLastRow && currentIndex === totalCells - 1 && "rounded-br-lg",
      isFirstRow && currentIndex === totalCells - 1 && "rounded-tr-lg",
      isFirstRow && "border-t border-t-neutral-300",
      !isLastRow && "border-b border-b-separator"
    );
  };

  const rowContent = (
    <tr
      className={cn(
        "group bg-white hover:bg-neutral-50",
        classNames.Line,
        isRootItem ? classNames.RootLine : classNames.ItemLine
      )}
      onClick={() => clickHierarchyRecord(item)}
    >
      {(visibleHierarchyColumns || []).map((column, colIndex) => {
        const { key, render } = column;
        const value = render?.(item) || item?.[key as keyof HierarchyItem];
        return (
          <DataManColumnProvider column={column} key={column.key}>
            <DataManCellProvider key={key} value={value}>
              <td
                style={
                  !colIndex && depth > 0
                    ? { paddingLeft: `${depth * 10}px` }
                    : undefined
                }
                className={getHierarchyCellClassName(colIndex, false)}
              >
                <DataManCellContent
                  hasCollapseControl={canShowCollapseControl && !colIndex}
                />
              </td>
            </DataManCellProvider>
          </DataManColumnProvider>
        );
      })}
      {hasHierarchyActions && (
        <DataManColumnProvider isActionColumn>
          <DataManCellProvider
            key={"action"}
            value={<DataManHierarchyRowActions />}
          >
            <td
              className={getHierarchyCellClassName(totalCells - 1, true)}
              style={
                actionsColumnWidth
                  ? {
                      width:
                        typeof actionsColumnWidth === "number"
                          ? `${actionsColumnWidth}px`
                          : actionsColumnWidth,
                      maxWidth:
                        typeof actionsColumnWidth === "number"
                          ? `${actionsColumnWidth}px`
                          : actionsColumnWidth
                    }
                  : undefined
              }
            >
              <DataManCellContent />
            </td>
          </DataManCellProvider>
        </DataManColumnProvider>
      )}
    </tr>
  );

  // Combine refs for drag handle and width capture
  const setHierarchyRefs = (
    el: HTMLTableRowElement | null,
    provided: DraggableProvided
  ) => {
    if (rowRef) rowRef.current = el;
    if (provided?.innerRef) provided.innerRef(el);
  };

  const row = hasSortableDataColumns ? (
    <DataManRowProvider data={item}>
      <Draggable draggableId={`${item.id}-`} index={index} key={`${item.id}-`}>
        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
          let cellIndex = 0;
          return (
            <tr
              ref={(el) => setHierarchyRefs(el, provided)}
              {...provided.draggableProps}
              className={cn(
                "group bg-white hover:bg-neutral-50",
                classNames.Line,
                isRootItem ? classNames.RootLine : classNames.ItemLine,
                snapshot.isDragging ? "table bg-blue-950 text-white" : ""
              )}
              onClick={() => clickHierarchyRecord(item)}
              onMouseDown={handleMouseDown}
            >
              <DataManColumnProvider isSortableHandler>
                <DataManCellProvider value={<DataManSortableHandler />}>
                  <td
                    {...withoutButtonRole(provided.dragHandleProps)}
                    aria-label="Reorder"
                    className={getHierarchyCellClassName(
                      cellIndex++,
                      false,
                      true
                    )}
                  >
                    <DataManCellContent />
                  </td>
                </DataManCellProvider>
              </DataManColumnProvider>
              {(visibleHierarchyColumns || []).map((column, colIndex) => {
                const { key, render } = column;
                const value =
                  render?.(item) || item?.[key as keyof HierarchyItem];
                const currentCellIndex = cellIndex++;
                return (
                  <DataManColumnProvider column={column} key={column.key}>
                    <DataManCellProvider key={key} value={value}>
                      <td
                        style={
                          !colIndex && depth > 0
                            ? { paddingLeft: `${depth * 10}px` }
                            : undefined
                        }
                        className={getHierarchyCellClassName(
                          currentCellIndex,
                          false
                        )}
                      >
                        <DataManCellContent
                          hasCollapseControl={
                            isHierarchyCollapsable && !colIndex
                          }
                        />
                      </td>
                    </DataManCellProvider>
                  </DataManColumnProvider>
                );
              })}
              {hasHierarchyActions && (
                <DataManColumnProvider isActionColumn>
                  <DataManCellProvider
                    key={"action"}
                    value={<DataManHierarchyRowActions />}
                  >
                    <td
                      className={getHierarchyCellClassName(
                        totalCells - 1,
                        true
                      )}
                      style={
                        actionsColumnWidth
                          ? {
                              width:
                                typeof actionsColumnWidth === "number"
                                  ? `${actionsColumnWidth}px`
                                  : actionsColumnWidth,
                              maxWidth:
                                typeof actionsColumnWidth === "number"
                                  ? `${actionsColumnWidth}px`
                                  : actionsColumnWidth
                            }
                          : undefined
                      }
                    >
                      <DataManCellContent />
                    </td>
                  </DataManCellProvider>
                </DataManColumnProvider>
              )}
            </tr>
          );
        }}
      </Draggable>
    </DataManRowProvider>
  ) : (
    <DataManRowProvider data={item}>{rowContent}</DataManRowProvider>
  );

  const tableRef = React.useRef<HTMLTableElement>(null);
  const headerRef = React.useRef<HTMLTableSectionElement>(null);
  //const fixedHeaderRef = React.useRef<HTMLTableSectionElement>(null);

  useDataTableFixedHeader({
    headerRef,
    fixedHeaderRef: headerRef,
    tableRef,
    containerRef,
    gap: 51
  });

  const colSpan = totalCells;

  return (
    <>
      {row}
      {isRowExpanded && item.items.length > 0 ? (
        <tr>
          <td className={cn(classNames.ItemsContainer)} colSpan={colSpan}>
            <div
              className={"border-l-neutral-200"}
              style={{ borderLeftWidth: (depth + 1) * 10 }}
            >
              {hasSortableDataColumns ? (
                <Droppable
                  droppableId={`content-table-${item.id}`}
                  type={"dataDroppable"}
                >
                  {(droppableProvided) => (
                    <table
                      ref={tableRef}
                      cellPadding={0}
                      cellSpacing={0}
                      className={"w-full border-separate table-fixed"}
                    >
                      <DataManTableViewHeader ref={headerRef} />
                      <tbody
                        ref={(ref: HTMLElement | null) => {
                          droppableProvided.innerRef(ref);
                        }}
                        {...droppableProvided.droppableProps}
                      >
                        {item.items.map((item: unknown, index: number) => (
                          <DataManTableViewRow
                            data={item}
                            key={(item as Dict).id}
                            index={index}
                          />
                        ))}
                        {droppableProvided.placeholder}
                      </tbody>
                    </table>
                  )}
                </Droppable>
              ) : (
                <table
                  ref={tableRef}
                  cellPadding={0}
                  cellSpacing={0}
                  className={"w-full border-separate table-fixed"}
                >
                  <DataManTableViewHeader ref={headerRef} />
                  <tbody>
                    {item.items.map((item: unknown, index: number) => (
                      <DataManTableViewRow
                        data={item}
                        key={(item as Dict).id}
                        index={index}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      ) : isRowExpanded && !item.children.length && hierarchyEmptyView ? (
        <tr>
          <td className={cn(classNames.ItemsContainer)} colSpan={colSpan}>
            {hierarchyEmptyView(item)}
          </td>
        </tr>
      ) : null}
    </>
  );
};

const DataManTableViewHierarchyRecords: React.FC = () => {
  const { hierarchyData, openHierarchyItems, maxHierarchyDepth } = useDataMan();
  const sanitizedHierarchyData = JSON.stringify(hierarchyData);
  const sanitizedOpenHierarchyItems = JSON.stringify(openHierarchyItems);
  return React.useMemo(() => {
    const records: React.ReactNode[] = [];
    let index = 0;

    // First, collect all visible items to determine first/last
    const visibleItems: HierarchyItem[] = [];
    const collectVisible = (item: HierarchyItem, currentDepth: number) => {
      visibleItems.push(item);
      // Only collect children if we haven't reached max depth
      if (
        openHierarchyItems?.includes(item.id) &&
        (maxHierarchyDepth === undefined || currentDepth < maxHierarchyDepth)
      ) {
        item.children?.forEach((child) =>
          collectVisible(child, currentDepth + 1)
        );
      }
    };
    hierarchyData?.forEach((item) => {
      collectVisible(item, 0);
    });

    const totalVisibleItems = visibleItems.length;

    // Then render with first/last information (index is 0-based for drag & drop)
    const iterate = (item: HierarchyItem, depth: number) => {
      const currentIndex = index;
      const isFirstRow = currentIndex === 0;
      const isLastRow = currentIndex === totalVisibleItems - 1;
      records.push(
        <HierarchyItemItem
          item={item}
          key={item.id}
          depth={depth}
          index={currentIndex}
          isFirstRow={isFirstRow}
          isLastRow={isLastRow}
        />
      );
      index++;
      // Only render children if we haven't reached max depth
      if (
        openHierarchyItems?.includes(item.id) &&
        (maxHierarchyDepth === undefined || depth < maxHierarchyDepth)
      ) {
        item.children?.forEach((item) => iterate(item, depth + 1));
      }
    };
    hierarchyData?.forEach((item) => {
      iterate(item, 0);
    });

    return <>{records}</>;
  }, [sanitizedHierarchyData, sanitizedOpenHierarchyItems, maxHierarchyDepth]);
};

const DataManTableViewSimpleRecords: React.FC = () => {
  const { data, dataIdKey } = useDataMan();
  const idKey = dataIdKey || "id";

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <>
      {data.map((item: any, index: number) => {
        const itemId = item[idKey] || item.id || index;
        return <DataManTableViewRow data={item} key={itemId} index={index} />;
      })}
    </>
  );
};

// Item data passed to react-window for efficient rendering
interface VirtualizedItemData {
  // biome-ignore lint/suspicious/noExplicitAny: data can be any shape
  items: any[];
  idKey: string;
  totalCount: number;
  // biome-ignore lint/suspicious/noExplicitAny: columns can have any data type
  visibleColumns: any[];
  hasBulkSelection: boolean;
  hasActions: boolean;
  showSelectedBackground: boolean;
  isRecordClickable: boolean;
  selectedItemsSet: Set<string>;
  // biome-ignore lint/suspicious/noExplicitAny: data can be any shape
  onRowClick: (data: any) => void;
  onToggleSelect: (itemId: string) => void;
  actionsColumnWidth?: number | string;
}

// Simple checkbox component for virtualized rows (no context)
const VirtualizedCheckbox: React.FC<{
  itemId: string;
  isSelected: boolean;
  onToggle: (itemId: string) => void;
}> = React.memo(({ itemId, isSelected, onToggle }) => {
  const handleCheckedChange = React.useCallback(() => {
    onToggle(itemId);
  }, [itemId, onToggle]);

  const handleClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Checkbox
      checked={isSelected}
      onCheckedChange={handleCheckedChange}
      onClick={handleClick}
      aria-label="Select row"
      className={DATA_MAN_CHECKBOX_CLASS}
    />
  );
});
VirtualizedCheckbox.displayName = "VirtualizedCheckbox";

// Pre-computed border class combinations for performance
const borderClassesCache = new Map<string, string>();
const getBorderClasses = (
  cellIndex: number,
  totalCells: number,
  isFirst: boolean,
  isLast: boolean
): string => {
  const key = `${cellIndex}-${totalCells}-${isFirst}-${isLast}`;
  let cached = borderClassesCache.get(key);
  if (!cached) {
    cached = cn(
      cellIndex === 0 && "border-l border-l-neutral-300",
      cellIndex === totalCells - 1 && "border-r border-r-neutral-300",
      isLast && "border-b border-b-neutral-300",
      isFirst && cellIndex === 0 && "rounded-tl-lg",
      isLast && cellIndex === 0 && "rounded-bl-lg",
      isLast && cellIndex === totalCells - 1 && "rounded-br-lg",
      isFirst && cellIndex === totalCells - 1 && "rounded-tr-lg",
      isFirst && "border-t border-t-neutral-300",
      !isLast && "border-b border-b-separator"
    );
    borderClassesCache.set(key, cached);
  }
  return cached;
};

// Virtualized row component - simplified, no context providers
const VirtualizedRowRenderer = React.memo<
  ListChildComponentProps<VirtualizedItemData>
>(({ index, style, data: itemData }) => {
  const {
    items,
    idKey,
    totalCount,
    visibleColumns,
    hasBulkSelection,
    hasActions,
    showSelectedBackground,
    isRecordClickable,
    selectedItemsSet,
    onRowClick,
    onToggleSelect,
    actionsColumnWidth
  } = itemData;

  const item = items[index];
  const itemId = String(item?.[idKey] || item?.id || index);
  const isFirst = index === 0;
  const isLast = index === totalCount - 1;
  const isSelected = selectedItemsSet.has(itemId);
  const hasSelectedBg = isSelected && showSelectedBackground;

  const totalCells =
    (hasBulkSelection ? 1 : 0) + visibleColumns.length + (hasActions ? 1 : 0);

  const rowClassName = cn(
    "group flex items-center h-full",
    isRecordClickable && "cursor-pointer",
    hasSelectedBg
      ? "bg-neutral-50 hover:bg-neutral-200"
      : "bg-white hover:bg-neutral-50",
    isFirst && "rounded-t-lg",
    isLast && "rounded-b-lg"
  );

  const handleRowClick = React.useCallback(() => {
    onRowClick(item);
  }, [item, onRowClick]);

  const actionsWidthValue = actionsColumnWidth
    ? typeof actionsColumnWidth === "number"
      ? `${actionsColumnWidth}px`
      : actionsColumnWidth
    : undefined;

  let cellIndex = 0;

  return (
    <div style={style}>
      <div className={rowClassName} onClick={handleRowClick}>
        {hasBulkSelection && (
          <div
            className={cn(
              // Checkbox left-anchored at pl-4 (its position when it was
              // centered in w-14) so trimming the column from the right doesn't
              // shift it left.
              "flex-none w-[46px] h-full flex items-center justify-start pl-4",
              getBorderClasses(cellIndex++, totalCells, isFirst, isLast)
            )}
          >
            <VirtualizedCheckbox
              itemId={itemId}
              isSelected={isSelected}
              onToggle={onToggleSelect}
            />
          </div>
        )}
        {visibleColumns.map((column: any) => {
          const value = column.render ? column.render(item) : item[column.key];
          const currentIndex = cellIndex++;
          const Renderer = column.renderer;
          // Check if value is a React element (from column.render)
          const isReactElement = React.isValidElement(value);
          const widthValue = column.width
            ? typeof column.width === "number"
              ? `${column.width}px`
              : column.width
            : undefined;
          return (
            <div
              key={column.key}
              className={cn(
                "min-w-0 h-full flex items-center overflow-hidden px-3",
                widthValue ? "flex-shrink" : "flex-1",
                getBorderClasses(currentIndex, totalCells, isFirst, isLast)
              )}
              style={
                widthValue
                  ? { width: widthValue, maxWidth: widthValue }
                  : undefined
              }
            >
              {Renderer ? (
                <DataManCellProvider value={value}>
                  <Renderer />
                </DataManCellProvider>
              ) : isReactElement ? (
                value
              ) : (
                <span className="truncate">
                  {value != null ? String(value) : ""}
                </span>
              )}
            </div>
          );
        })}
        {hasActions && (
          <DataManRowProvider data={item}>
            <div
              className={cn(
                "flex-none h-full flex items-center justify-end px-3 whitespace-nowrap",
                !actionsWidthValue && "w-fit",
                getBorderClasses(cellIndex, totalCells, isFirst, isLast)
              )}
              style={
                actionsWidthValue
                  ? { width: actionsWidthValue, maxWidth: actionsWidthValue }
                  : undefined
              }
            >
              <DataManRowActions />
            </div>
          </DataManRowProvider>
        )}
      </div>
    </div>
  );
});
VirtualizedRowRenderer.displayName = "VirtualizedRowRenderer";

// The list scrolls by itself: it takes focus so the keyboard can scroll rows that have
// nothing focusable in them
const VirtualizedListOuter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => (
  <div
    ref={ref}
    // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling region must be reachable from the keyboard
    tabIndex={0}
    {...props}
    className={cn(
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset",
      props.className
    )}
  />
));
VirtualizedListOuter.displayName = "VirtualizedListOuter";

interface VirtualizedTableProps {
  height: number;
  onScroll?: (scrollOffset: number) => void;
}

const DataManTableViewVirtualizedRecords: React.FC<VirtualizedTableProps> = ({
  height,
  onScroll
}) => {
  const {
    data,
    dataIdKey,
    virtualizationOptions,
    visibleColumns,
    hasBulkSelection,
    hasActions,
    showSelectedBackground,
    isRecordClickable,
    selectedItems,
    clickRecord,
    toggleSelectItem,
    actionsColumnWidth
  } = useDataMan();

  const idKey = dataIdKey || "id";
  const rowHeight = virtualizationOptions?.rowHeight ?? 64;
  const overscanCount = virtualizationOptions?.overscanCount ?? 5;

  // Convert selectedItems array to Set for O(1) lookups - memoized
  const selectedItemsSet = React.useMemo(
    () => new Set(selectedItems),
    [selectedItems]
  );

  // Memoize callbacks to prevent re-renders
  const onRowClick = React.useCallback(
    (item: any) => {
      clickRecord(item);
    },
    [clickRecord]
  );

  const onToggleSelect = React.useCallback(
    (itemId: string) => {
      toggleSelectItem(itemId);
    },
    [toggleSelectItem]
  );

  // Memoize itemData to prevent unnecessary re-renders
  const itemData: VirtualizedItemData = React.useMemo(
    () => ({
      items: data || [],
      idKey,
      totalCount: data?.length || 0,
      visibleColumns: visibleColumns || [],
      hasBulkSelection: hasBulkSelection || false,
      hasActions: hasActions || false,
      showSelectedBackground: showSelectedBackground !== false,
      isRecordClickable: isRecordClickable || false,
      selectedItemsSet,
      onRowClick,
      onToggleSelect,
      actionsColumnWidth
    }),
    [
      data,
      idKey,
      visibleColumns,
      hasBulkSelection,
      hasActions,
      showSelectedBackground,
      isRecordClickable,
      selectedItemsSet,
      onRowClick,
      onToggleSelect,
      actionsColumnWidth
    ]
  );

  const handleScroll = React.useCallback(
    ({ scrollOffset }: { scrollOffset: number }) => {
      onScroll?.(scrollOffset);
    },
    [onScroll]
  );

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <FixedSizeList<VirtualizedItemData>
      height={height}
      width="100%"
      itemCount={data.length}
      itemSize={rowHeight}
      overscanCount={overscanCount}
      itemData={itemData}
      onScroll={handleScroll}
      outerElementType={VirtualizedListOuter}
    >
      {VirtualizedRowRenderer}
    </FixedSizeList>
  );
};

// Div-based header for virtualized table (matches flex layout of virtualized rows)
interface VirtualizedHeaderProps {
  className?: string;
}

const DataManVirtualizedHeader = React.forwardRef<
  HTMLDivElement,
  VirtualizedHeaderProps
>(({ className }, ref) => {
  const {
    hasSortableDataColumns,
    hasBulkSelection,
    visibleColumns,
    hasActions,
    actionsColumnWidth
  } = useDataMan();

  const actionsWidthValue = actionsColumnWidth
    ? typeof actionsColumnWidth === "number"
      ? `${actionsColumnWidth}px`
      : actionsColumnWidth
    : undefined;

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center bg-[#F7F9FB] border-y border-[rgba(20,29,38,0.05)]",
        className
      )}
    >
      {hasBulkSelection && (
        <DataManColumnProvider isSelectionColumn>
          <div className="flex-none w-[46px]">
            <DataManHeaderColumnContent />
          </div>
        </DataManColumnProvider>
      )}
      {visibleColumns?.map((column, index) => {
        const isLastColumn =
          !hasActions && index === (visibleColumns?.length || 0) - 1;
        const widthValue = column.width
          ? typeof column.width === "number"
            ? `${column.width}px`
            : column.width
          : undefined;
        return (
          <DataManColumnProvider
            column={column}
            isOptionsColumn={isLastColumn}
            key={column.key}
          >
            <div
              className={cn(
                "min-w-0 overflow-hidden",
                widthValue ? "flex-shrink" : "flex-1"
              )}
              style={
                widthValue
                  ? { width: widthValue, maxWidth: widthValue }
                  : undefined
              }
            >
              <DataManHeaderColumnContent />
            </div>
          </DataManColumnProvider>
        );
      })}
      {hasActions && (
        <DataManColumnProvider isOptionsColumn isActionColumn>
          <div
            className={cn("flex-none px-3", !actionsWidthValue && "w-fit")}
            style={
              actionsWidthValue
                ? { width: actionsWidthValue, maxWidth: actionsWidthValue }
                : undefined
            }
          >
            <DataManHeaderColumnContent />
          </div>
        </DataManColumnProvider>
      )}
    </div>
  );
});
DataManVirtualizedHeader.displayName = "DataManVirtualizedHeader";

// Group header component for grouped records
interface DataManGroupHeaderProps {
  group: {
    label: string;
    shortLabel?: string;
    color?: string;
  };
  colSpan: number;
}

const DataManGroupHeader: React.FC<DataManGroupHeaderProps> = ({
  group,
  colSpan
}) => {
  return (
    <tr>
      <td colSpan={colSpan} className="border-t border-t-neutral-300">
        <div className="flex items-center gap-4 p-4 font-bold text-sm">
          {(group.shortLabel || group.color) && (
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full font-normal text-white"
              style={{ backgroundColor: group.color || "#6B7280" }}
            >
              {group.shortLabel || ""}
            </div>
          )}
          {group.label}
        </div>
      </td>
    </tr>
  );
};

// Grouped records component for rendering data in groups
const DataManTableViewGroupedRecords: React.FC = () => {
  const {
    groupedData,
    dataIdKey,
    visibleColumns,
    hasBulkSelection,
    hasActions,
    hasSortableDataColumns
  } = useDataMan();
  const idKey = dataIdKey || "id";

  if (!groupedData || groupedData.length === 0) {
    return null;
  }

  // Calculate total columns for group header
  const totalColumns =
    (hasSortableDataColumns ? 1 : 0) +
    (hasBulkSelection ? 1 : 0) +
    (visibleColumns?.length || 0) +
    (hasActions ? 1 : 0);

  return (
    <>
      {groupedData.map((groupData) => {
        const { group, items } = groupData;
        if (!items || items.length === 0) return null;

        return (
          <React.Fragment key={group.id}>
            <DataManGroupHeader group={group} colSpan={totalColumns} />
            {items.map((item: any, index: number) => {
              const itemId = item[idKey] || item.id || index;
              return (
                <DataManTableViewRow
                  data={item}
                  key={`${group.id}-${itemId}`}
                  index={index}
                />
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

export const DataManTableViewWrapper: React.FC = () => {
  const {
    containerRef,
    hasSortableDataColumns,
    enableHierarchy,
    hasGroupBy,
    groupedData,
    data,
    hierarchyData,
    fullSize,
    fullBleed,
    enableVirtualization,
    virtualizationOptions,
    emptyState,
    minTableWidth
  } = useDataMan();

  const tableRef = React.useRef<HTMLTableElement>(null);
  const headerRef = React.useRef<HTMLTableSectionElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  // Ref to store captured column widths for drag preview
  const dragWidthsRef = React.useRef<number[]>([]);
  // State for virtualized container height
  const [virtualContainerHeight, setVirtualContainerHeight] =
    React.useState<number>(0);
  const virtualContainerRef = React.useRef<HTMLDivElement>(null);
  const virtualizedHeaderRef = React.useRef<HTMLDivElement>(null);
  // Track scroll state for header border
  const [isScrolled, setIsScrolled] = React.useState(false);

  // Measure container height for virtualization
  React.useLayoutEffect(() => {
    if (!enableVirtualization || !virtualContainerRef.current) return;

    const measureHeight = () => {
      if (virtualContainerRef.current) {
        const rect = virtualContainerRef.current.getBoundingClientRect();
        // Subtract header height
        const headerHeight =
          virtualizedHeaderRef.current?.getBoundingClientRect().height || 0;
        setVirtualContainerHeight(rect.height - headerHeight);
      }
    };

    // Delay initial measurement to ensure header is rendered
    const timeoutId = setTimeout(measureHeight, 0);

    // Use ResizeObserver for responsive height updates
    const resizeObserver = new ResizeObserver(measureHeight);
    resizeObserver.observe(virtualContainerRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [enableVirtualization]);

  // Use explicit height from options if provided
  const effectiveVirtualHeight =
    virtualizationOptions?.height ?? virtualContainerHeight;

  // Track scroll position for header border in fullSize mode
  React.useEffect(() => {
    if (!fullSize) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      setIsScrolled(scrollContainer.scrollTop > 0);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [fullSize]);

  // Only use fixed header when not in fullSize mode (fullSize uses sticky header instead)
  useDataTableFixedHeader({
    headerRef,
    fixedHeaderRef: headerRef,
    tableRef,
    containerRef: fullSize ? undefined : containerRef
  });

  // Use hierarchy header when hierarchy is enabled, otherwise use simple header
  const HeaderComponent = enableHierarchy
    ? DataManTableViewHierarchyHeader
    : DataManTableViewHeader;

  // Use hierarchy records when hierarchy is enabled, grouped records when groupBy is enabled, otherwise simple records
  const RecordsComponent = enableHierarchy
    ? DataManTableViewHierarchyRecords
    : hasGroupBy
      ? DataManTableViewGroupedRecords
      : DataManTableViewSimpleRecords;

  // Check for data presence based on mode
  const hasData = enableHierarchy
    ? hierarchyData && hierarchyData.length > 0
    : hasGroupBy
      ? groupedData && groupedData.length > 0
      : data && data.length > 0;

  // Full size mode: table takes full height with internal scrolling and sticky header
  // Note: shadow is on the outer wrapper when fullSize to include the footer
  const containerClassName = fullSize
    ? cn(
        "flex flex-col h-full overflow-hidden bg-white",
        !fullBleed && "rounded-t-lg"
      )
    : "overflow-hidden rounded-t-lg bg-white shadow-md";

  const scrollWrapperClassName = fullSize
    ? cn(
        "flex-1 overflow-y-auto flex flex-col",
        minTableWidth ? "overflow-x-auto" : "overflow-x-hidden"
      )
    : "";

  const tableMinWidthStyle = minTableWidth
    ? {
        minWidth:
          typeof minTableWidth === "number"
            ? `${minTableWidth}px`
            : minTableWidth
      }
    : undefined;

  const theadClassName = cn(
    fullSize
      ? "sticky top-0 z-10 bg-[#F7F9FB] border-y border-[rgba(20,29,38,0.05)]"
      : "relative z-[9] bg-[#F7F9FB] border-y border-[rgba(20,29,38,0.05)]",
    fullSize && isScrolled && "border-b border-neutral-300"
  );

  // Virtualization is only supported for non-hierarchy, non-sortable tables
  const canUseVirtualization =
    enableVirtualization && !enableHierarchy && !hasSortableDataColumns;

  // Wrapper class for fullSize mode with footer support
  // Shadow is on the outer wrapper to include both table and footer
  const fullSizeWrapperClassName = fullSize
    ? cn(
        "flex flex-col h-full overflow-hidden absolute inset-0",
        !fullBleed && "shadow-md rounded-lg"
      )
    : "";

  // Empty state component - centered in full size mode
  const EmptyStateContent = emptyState ? (
    <div
      className={cn(
        "flex items-center justify-center bg-white",
        fullSize ? "flex-1" : "py-12"
      )}
    >
      {emptyState}
    </div>
  ) : null;

  const tableContent = canUseVirtualization ? (
    <div className={fullSizeWrapperClassName}>
      <div
        ref={virtualContainerRef}
        className={cn(containerClassName, fullSize && "flex-1 min-h-0")}
      >
        <div
          className={cn(
            "mx-0",
            !fullBleed && "rounded-lg",
            fullSize && "flex-1 flex flex-col min-h-0"
          )}
        >
          {/* Div-based header for virtualized table */}
          <DataManVirtualizedHeader
            ref={virtualizedHeaderRef}
            className={cn(
              "sticky top-0 z-10",
              isScrolled && "border-b border-neutral-300"
            )}
          />
          {/* Virtualized body or empty state */}
          {hasData ? (
            <div className="flex-1 min-h-0">
              {effectiveVirtualHeight > 0 && (
                <DataManTableViewVirtualizedRecords
                  height={effectiveVirtualHeight}
                  onScroll={(offset) => setIsScrolled(offset > 0)}
                />
              )}
            </div>
          ) : (
            EmptyStateContent
          )}
        </div>
        <DataManLoadMore />
      </div>
      <DataManBulkActionsFooter />
    </div>
  ) : (
    <div className={fullSizeWrapperClassName}>
      <div className={cn(containerClassName, fullSize && "flex-1 min-h-0")}>
        <div
          className={cn(
            "mx-0",
            !fullBleed && "rounded-lg",
            fullSize && "flex-1 flex flex-col min-h-0"
          )}
        >
          <div ref={scrollContainerRef} className={scrollWrapperClassName}>
            <table
              ref={tableRef}
              cellPadding={0}
              cellSpacing={0}
              className={
                "relative table w-full border-separate bg-white table-fixed"
              }
              style={tableMinWidthStyle}
            >
              <HeaderComponent ref={headerRef} className={theadClassName} />
              <tbody>{hasData ? <RecordsComponent /> : null}</tbody>
            </table>
            {!hasData && EmptyStateContent}
          </div>
        </div>
        <DataManLoadMore />
      </div>
      <DataManBulkActionsFooter />
    </div>
  );

  if (hasSortableDataColumns) {
    // renderClone creates a custom drag preview that doesn't have the max-w-0 issue
    const renderClone = (
      provided: DraggableProvided,
      snapshot: DraggableStateSnapshot,
      rubric: DraggableRubric
    ) => {
      const draggableId = rubric.draggableId;

      // Check if this is a hierarchy item (ends with -)
      if (draggableId.endsWith("-")) {
        const itemId = draggableId.slice(0, -1);
        // Find the item in hierarchyData recursively, tracking depth
        const findInHierarchy = (
          items: HierarchyItem[] | undefined,
          currentDepth: number
        ): { item: HierarchyItem; depth: number } | null => {
          if (!items) return null;
          for (const item of items) {
            if (item.id === itemId) return { item, depth: currentDepth };
            const found = findInHierarchy(item.children, currentDepth + 1);
            if (found) return found;
          }
          return null;
        };
        const result = findInHierarchy(hierarchyData || [], 0);
        if (!result) return null;
        return (
          <HierarchyDragCloneRow
            data={result.item}
            provided={provided}
            depth={result.depth}
          />
        );
      }

      // Regular data item
      const draggedItem = data?.find((item: Dict) => item.id === draggableId);
      if (!draggedItem) return null;
      return <DragCloneRow data={draggedItem} provided={provided} />;
    };

    return (
      <DragWidthsContext.Provider value={dragWidthsRef}>
        <div className={fullSizeWrapperClassName}>
          <div className={cn(containerClassName, fullSize && "flex-1 min-h-0")}>
            <div
              className={cn(
                "mx-0 rounded-lg border",
                fullSize && "flex-1 flex flex-col min-h-0"
              )}
            >
              <div ref={scrollContainerRef} className={scrollWrapperClassName}>
                <Droppable
                  droppableId={"content-table"}
                  type="hierarchyDroppable"
                  renderClone={renderClone}
                >
                  {(droppableProvided) => (
                    <table
                      ref={tableRef}
                      cellPadding={0}
                      cellSpacing={0}
                      className={
                        "relative table w-full border-separate bg-white table-fixed"
                      }
                    >
                      <HeaderComponent
                        ref={headerRef}
                        className={theadClassName}
                      />
                      <tbody
                        ref={(ref: HTMLElement | null) => {
                          droppableProvided.innerRef(ref);
                        }}
                        {...droppableProvided.droppableProps}
                      >
                        {hasData ? <RecordsComponent /> : null}
                        {droppableProvided.placeholder}
                      </tbody>
                    </table>
                  )}
                </Droppable>
                {!hasData && EmptyStateContent}
              </div>
            </div>
            <DataManLoadMore />
          </div>
          <DataManBulkActionsFooter />
        </div>
      </DragWidthsContext.Provider>
    );
  }

  return tableContent;
};
